import { s, type SpacingToken } from '@/theme/tokens';
import { Box, Stack, Surface, Text } from '@/ui';
import { Inline } from '@/ui/layout/Inline';
import type { SxProps, Theme } from '@/ui/primitives/types';
import type { ReactElement, ReactNode } from 'react';

export type FormCardProps = {
  title?: string;
  titleSlot?: ReactNode;
  titleSx?: SxProps<Theme>;
  description?: ReactNode;
  topSlot?: ReactNode;
  headerSlot?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  padding?: SpacingToken | null;
  gap?: SpacingToken;
  contentGap?: SpacingToken;
  headerGap?: SpacingToken;
  footerGap?: SpacingToken;
  container?: 'surface' | 'none';
  sx?: SxProps<Theme>;
};

export const FormCard = ({
  title,
  titleSlot,
  titleSx,
  description,
  topSlot,
  headerSlot,
  footer,
  children,
  padding = 'lg',
  gap = 'md',
  contentGap = 'md',
  headerGap = 'sm',
  footerGap = 'md',
  container = 'surface',
  sx,
}: FormCardProps): ReactElement => {
  const resolvedTitleSx = titleSx
    ? ([{ pl: s('none') }, ...(Array.isArray(titleSx) ? titleSx : [titleSx])] as SxProps<Theme>)
    : ({ pl: s('none') } as SxProps<Theme>);
  const hasHeader = Boolean(title ?? description ?? headerSlot);
  const hasContent = children !== null && children !== undefined && children !== false;

  const content = (
    <Stack gap={gap}>
      {topSlot ? <Box>{topSlot}</Box> : null}
      {hasHeader ? (
        <Stack gap={headerGap}>
          {title ? (
            titleSlot ? (
              <Inline justify="space-between" align="center">
                <Text role="sectionTitle" sx={resolvedTitleSx}>
                  {title}
                </Text>
                <Box>{titleSlot}</Box>
              </Inline>
            ) : (
              <Text role="sectionTitle" sx={resolvedTitleSx}>
                {title}
              </Text>
            )
          ) : null}
          {description ? (
            typeof description === 'string' ? (
              <Text
                role="body"
                color="textSecondary"
                sx={{
                  fontSize: (theme) => theme.typography.subtitle1.fontSize,
                  lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
                }}
              >
                {description}
              </Text>
            ) : (
              description
            )
          ) : null}
          {headerSlot ? <Box>{headerSlot}</Box> : null}
        </Stack>
      ) : null}
      {hasContent ? <Stack gap={contentGap}>{children}</Stack> : null}
      {footer ? (
        <Box
          sx={{
            mt: s(footerGap),
            fontSize: (theme) => theme.typography.subtitle1.fontSize,
            lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Stack>
  );

  if (container === 'none') {
    const baseSx = padding ? ({ p: s(padding) } satisfies SxProps<Theme>) : null;
    const resolvedSx = sx
      ? ([baseSx, ...(Array.isArray(sx) ? sx : [sx])].filter(Boolean) as SxProps<Theme>)
      : (baseSx ?? undefined);
    return <Box sx={resolvedSx}>{content}</Box>;
  }

  return (
    <Surface padding={padding} sx={sx}>
      {content}
    </Surface>
  );
};
